import React, { useState, useEffect, useContext } from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Skeleton,
    Collapse,
    IconButton,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAllEventsQuery } from '@query/eventQuery';
import { useClubEventAllQuery } from "@query/clubQuery";
import { mapRewardedActivityEvent } from '@service/rewardedActivityService';
import { AccountContext } from "@context/AccountProvider";

// 👈 1. Imports for the new functionality
// Please verify these paths match your project structure.
import SidePanel from '@component/SidePanel';
import ClubEventForm from '@component/ClubEventForm';


const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

function getRewardByEventUuid(eventsArray, eventUuid) {
    const foundEvent = eventsArray.find(eventData => eventData.event.uuid === eventUuid);
    if (foundEvent) {
        return foundEvent.reward;
    } else {
        return null;
    }
}

export default function RewardedActivitiesPage() {
    const { t } = useTranslation(['main', 'rewarded_activity']);
    const { club } = useContext(AccountContext);
    const [rewardedEvents, setRewardedEvents] = useState([]);
    const [expanded, setExpanded] = useState({});

    // 👈 2. State for side panel visibility and selected event
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedEventUuid, setSelectedEventUuid] = useState(null);

    const allEventsQuery = useAllEventsQuery();
    const clubEventAllQuery = useClubEventAllQuery(club?.uuid);

    // 👈 3. Modify handleExpandClick to prevent opening the panel simultaneously
    const handleExpandClick = (e, uuid) => {
        e.stopPropagation(); // Prevents the card's onClick from firing
        setExpanded((prev) => ({
            ...prev,
            [uuid]: !prev[uuid],
        }));
    };

    // 👈 4. Add handlers for opening and closing the side panel
    const handleCardClick = (uuid) => {
        setSelectedEventUuid(uuid);
        setPanelOpen(true);
    };

    const handlePanelClose = () => {
        setPanelOpen(false);
        setSelectedEventUuid(null);
        // Refetch data in case the reward was updated in the form
        clubEventAllQuery.refetch();
    };

    useEffect(() => {
        if (allEventsQuery.data?.data && clubEventAllQuery.data?.data) {
            const mappedEvents = allEventsQuery.data.data
                .map((event) => {
                    const mapped = mapRewardedActivityEvent(event.name);
                    const reward = getRewardByEventUuid(clubEventAllQuery.data?.data, event.uuid);
                    if (!mapped) return null;

                    const title = t(`rewardedActivity:${mapped.translationKey}`);
                    const description = t(`rewardedActivity:description.${mapped.translationKey}`);

                    return {
                        ...event,
                        mappedData: mapped,
                        title,
                        reward: reward,
                        description,
                    };
                })
                .filter(Boolean)
                .sort((a, b) => a.title.localeCompare(b.title));

            setRewardedEvents(mappedEvents);
        }
    }, [allEventsQuery.data, clubEventAllQuery.data, t]);

    return (
        <Box
            sx={{
                width: {
                    xs: '100%',
                    sm: '100%',
                    md: '100%',
                    lg: '80%',
                    xl: '60%',
                },
                pb: 4,
                margin: 'auto'
            }}
        >
            {/* 👈 5. Add the SidePanel component */}
            <SidePanel
            width={700} minWidth={700}
                open={panelOpen}
                onClose={handlePanelClose}
                title={t('rewardedActivity:activityDetail')}
                closeOnOutsideClick={true}
            >
                {/* Conditionally render form to ensure uuid is available */}
                {selectedEventUuid && <ClubEventForm eventUuid={selectedEventUuid} onCancel={handlePanelClose}/>}
            </SidePanel>

            <Typography variant="h2" mb={1}>
                {t('rewardedActivity:title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
                {t('rewardedActivity:subtitle')}
            </Typography>

            <Grid container spacing={3}>
                {allEventsQuery.isFetching
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 4 }} key={index}>
                            <Card>
                                <CardContent>
                                    <Skeleton height={30} width="60%" />
                                    <Skeleton height={20} width="90%" />
                                    <Skeleton height={20} width="70%" />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                    : rewardedEvents.map((event) => (
                        // The Grid item is unchanged, using the `size` prop as in your original code.
                        <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 6 }} key={event.uuid}>
                            <Card
                                variant="outlined"
                                onClick={() => handleCardClick(event.uuid)} // 👈 6. Add onClick to the Card
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 0,
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.2s ease',
                                    '&:hover': { boxShadow: 4 },
                                }}
                            >
                                <CardContent>
                                    <Box mb={2} sx={{ position: "relative" }}>
                                        <img
                                            src={event.mappedData.imageSrc}
                                            alt={event.title}
                                            style={{
                                                width: '100%',
                                                height: '250px',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ px: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                                        <Typography variant="h5" fontWeight={600}>
                                            {event.title}
                                        </Typography>
                                        {!expanded[event.uuid] &&
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ display: 'block', mt: 0 }}
                                            >
                                                {event.description.slice(0, 200)}
                                                {event.description.length > 200 && '...'}
                                            </Typography>
                                        }
                                        <Collapse in={expanded[event.uuid]} timeout="auto" unmountOnExit>
                                            {expanded[event.uuid] &&
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 1 }}
                                                >
                                                    {event.description}
                                                </Typography>
                                            }
                                        </Collapse>
                                        <Box sx={{ display: "flex", justifyContent: "right" }}>
                                            {event.reward ? (
                                                <Chip
                                                    color="primary"
                                                    sx={{ py: 1 }}
                                                    label={t('rewardedActivity:pointsDisplay', { points: event.reward })}
                                                    onClick={()=>{}}
                                                />
                                            ) : (
                                                <Chip
                                                    color="secondary"
                                                    sx={{ py: 1 }}
                                                    label={t('rewardedActivity:noReward', { points: event.reward })}
                                                    onClick={()=>{}}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>
                                <Box sx={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center", p: 1 }}>
                                    <ExpandMore
                                        sx={{ border: "none", m: 0 }}
                                        expand={expanded[event.uuid] || false}
                                        onClick={(e) => handleExpandClick(e, event.uuid)} // 👈 7. Update onClick call
                                        aria-expanded={expanded[event.uuid] || false}
                                        aria-label="show more"
                                    >
                                        <ExpandMoreIcon />
                                    </ExpandMore>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
            </Grid>
        </Box>
    );
}